import { useCallback, useEffect, useState } from "react";
import { Plus, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel } from "@/components/PageHeader";
import { ActivityLogsButton } from "@/components/ActivityLogs";

const RoleSelect = ({ roles, value, onChange, testId, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-8 w-48" data-testid={testId}><SelectValue placeholder="Role" /></SelectTrigger>
    <SelectContent>{Object.entries(roles).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

const NewUserDialog = ({ open, onOpenChange, roles, onCreated }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ role: "site_qs" }); }, [open]);
  const save = async () => {
    try { await api.post("/users", f); toast.success("User created"); onCreated(); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const inp = (k, l, type = "text") => {
    const common = { value: f[k] || "", onChange: (e) => setF((s) => ({ ...s, [k]: e.target.value })) };
    return (
      <div className="space-y-1.5"><Label>{l}</Label>
        {type === "password" ? <PasswordInput {...common} testId={`input-user-${k}`} /> : <Input type={type} {...common} data-testid={`input-user-${k}`} />}
      </div>
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-user">
        <DialogHeader><DialogTitle className="font-heading">New user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {inp("name", "Full name")}{inp("email", "Email", "email")}{inp("password", "Initial password (min 8)", "password")}
          <div className="space-y-1.5"><Label>Role</Label><RoleSelect roles={roles} value={f.role} onChange={(v) => setF((s) => ({ ...s, role: v }))} testId="select-new-user-role" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="btn-create-user">Create user</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PasswordReveal = ({ userId, email }) => {
  const [pw, setPw] = useState(null);
  const toggle = async () => {
    if (pw !== null) { setPw(null); return; }
    try { setPw((await api.get(`/users/${userId}/password`)).data.password); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-1">
      <span className="min-w-[90px] font-mono text-xs" data-testid={`password-${email}`}>{pw ?? "••••••••"}</span>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={toggle} title={pw ? "Hide password" : "Show password"} aria-label={pw ? "Hide password" : "Show password"} data-testid={`btn-show-pw-${email}`}>
        {pw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
};

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [newOpen, setNewOpen] = useState(false);
  const [pw, setPw] = useState(null);
  const isAdmin = user?.role === "admin";
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
    load();
  }, [load, isAdmin]);

  const upd = async (id, body, msg = "User updated") => {
    try { await api.put(`/users/${id}`, body); toast.success(msg); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  if (user.role !== "admin") return <p className="text-sm text-slate-500">Admin only.</p>;

  return (
    <div>
      <PageHeader dept="Administration" title="Users & Roles" subtitle="Accounts are created by admin only; each role controls which approvals a user can perform">
        <div className="flex gap-2">
          {user.can_view_logs && <ActivityLogsButton />}
          <Button onClick={() => setNewOpen(true)} data-testid="btn-new-user"><Plus className="mr-1.5 h-4 w-4" />New user</Button>
        </div>
      </PageHeader>
      <Panel>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{["Name", "Email", "Password", "Role", "Active", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`row-user-${u.email}`}>
                  <TableCell className="text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><PasswordReveal userId={u.id} email={u.email} /></TableCell>
                  <TableCell><RoleSelect roles={roles} value={u.role} disabled={u.id === user.id} onChange={(v) => upd(u.id, { role: v })} testId={`select-role-${u.email}`} /></TableCell>
                  <TableCell><Switch checked={u.active} disabled={u.id === user.id} onCheckedChange={(v) => upd(u.id, { active: v })} data-testid={`switch-active-${u.email}`} /></TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => setPw({ id: u.id, email: u.email, value: "" })} data-testid={`btn-reset-pw-${u.email}`}><KeyRound className="mr-1 h-3.5 w-3.5" />Reset password</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>
      <NewUserDialog open={newOpen} onOpenChange={setNewOpen} roles={roles} onCreated={load} />
      <Dialog open={!!pw} onOpenChange={(o) => !o && setPw(null)}>
        <DialogContent data-testid="dialog-reset-password">
          <DialogHeader><DialogTitle className="font-heading">Reset password · {pw?.email}</DialogTitle></DialogHeader>
          <PasswordInput placeholder="New password (min 8)" value={pw?.value || ""} onChange={(e) => setPw((p) => ({ ...p, value: e.target.value }))} testId="input-new-password" />
          <DialogFooter><Button onClick={async () => { if (await upd(pw.id, { password: pw.value }, "Password reset")) setPw(null); }} data-testid="btn-confirm-reset">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
